import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Video, PhoneOff } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });

const loadJitsi = () => new Promise((resolve, reject) => {
  if (window.JitsiMeetExternalAPI) return resolve();
  const s = document.createElement('script');
  s.src = 'https://meet.jit.si/external_api.js';
  s.async = true;
  s.onload = resolve;
  s.onerror = reject;
  document.body.appendChild(s);
});

const VideoDatePage = ({ user }) => {
  const { dateId } = useParams();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const [dateInfo, setDateInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/dates/${dateId}`, authHeaders());
        if (cancelled) return;
        setDateInfo(res.data);
        const fmt = res.data.format || res.data.mode;
        if (!(fmt === 'video' || fmt === 'audio') || !res.data.video_room) {
          setError('This meeting has no in-app call link.');
          return;
        }
        const isHost = res.data.is_host === true;
        const hostControlled = res.data.host_joins === true;
        await loadJitsi();
        if (cancelled || !containerRef.current) return;
        apiRef.current = new window.JitsiMeetExternalAPI('meet.jit.si', {
          roomName: res.data.video_room,
          parentNode: containerRef.current,
          width: '100%',
          height: '100%',
          userInfo: { displayName: user?.name || 'Guest' },
          configOverwrite: {
            prejoinPageEnabled: hostControlled && !isHost,   // participants wait/prep when a host chaperones
            disableDeepLinking: true,
            startAudioOnly: fmt === 'audio',
            startWithVideoMuted: fmt === 'audio',
          },
          interfaceConfigOverwrite: { MOBILE_APP_PROMO: false },
        });
        apiRef.current.addEventListener('readyToClose', () => navigate('/concierge'));
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Could not start the video date.');
      }
    })();
    return () => {
      cancelled = true;
      if (apiRef.current) { try { apiRef.current.dispose(); } catch (e) {} }
    };
  }, [dateId, user, navigate]);

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      <div className="flex items-center justify-between px-6 py-3 bg-gray-950 text-white">
        <div className="flex items-center gap-2">
          <Video className="w-5 h-5 text-rose-400" />
          <span className="font-semibold">{dateInfo?.meeting_type === 'interview' ? 'Interview' : dateInfo?.meeting_type === 'chaperoned' ? 'Chaperoned Intro' : 'Guided Date'}{dateInfo?.other ? ` · with ${dateInfo.other.name}` : ''}</span>
        </div>
        <Button data-testid="leave-video-btn" variant="ghost" className="text-rose-300 hover:bg-gray-800" onClick={() => navigate('/concierge')}>
          <PhoneOff className="w-4 h-4 mr-2" /> Leave
        </Button>
      </div>
      {error ? (
        <div className="flex-1 flex flex-col items-center justify-center text-gray-300 gap-4">
          <p data-testid="video-error">{error}</p>
          <Button variant="outline" onClick={() => navigate('/concierge')}><ArrowLeft className="w-4 h-4 mr-2" /> Back to Concierge</Button>
        </div>
      ) : (
        <div ref={containerRef} data-testid="video-container" className="flex-1" />
      )}
    </div>
  );
};

export default VideoDatePage;
